import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  operations as ssapiOperations,
  selectors as ssapiSelectors,
} from 'store/ssapiManager'
import { Formik } from 'formik'
import {
  additionalGoalFields,
  validationSchema,
  optionalFieldNames,
  requiredFieldNames,
  fieldNames,
  previousYearFieldNames,
} from './data'
import GoalsModalComponent from './component'
import {
  classCreationGoalValueName,
  lessonCompletionGoalValueName,
  schoolwideGoalIsDisabledName,
} from './AddGoals/data'
import { PLAYBOOK_STEP_MAP } from 'utils/adminExperienceHelpers'

export const GoalsModalContainer = ({
  areRequiredGoalsComplete,
  clearErrors,
  fetchPublishFlows,
  flowLabel,
  handleCancel,
  hasApiError,
  handleClose,
  hideOutro,
  modals,
  postPublishFlows,
  preferences,
  publishFlowExists,
  publishFlowId,
  updatePublishFlows,
  siteId,
  structureVersion,
}) => {
  const isMountedRef = useRef(true)

  useEffect(() => {
    if (siteId) {
      fetchPublishFlows(siteId)
    }
  }, [siteId])

  useEffect(() => {
    // Track mount status
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [preferences])
  const goal3HasValues =
    preferences?.Goal3Text?.length > 0 && preferences?.Goal3Date?.length > 0
  const goal4HasValues =
    preferences?.Goal4Text?.length > 0 && preferences?.Goal4Date?.length > 0
  const goal5HasValues =
    preferences?.Goal5Text?.length > 0 && preferences?.Goal5Date?.length > 0

  const showGoal3 = goal3HasValues || goal4HasValues || goal5HasValues || false
  const showGoal4 = goal4HasValues || goal5HasValues || false
  const showGoal5 = goal5HasValues || false
  const [currentStep, setCurrentStep] = useState(0)
  const [additionalGoalCount, setAdditionalGoalCount] = useState(
    showGoal3 + showGoal4 + showGoal5, // boolean javascript magic
  )

  const handleStepChange = toStep => setCurrentStep(toStep)

  const goBack = () => {
    handleStepChange(currentStep - 1)
  }

  const closeModal = () => {
    clearErrors()
    handleClose()
  }

  const handleSave = async (values, isComplete) => {
    if (additionalGoalCount === 2) {
      values.Goal5Text = ''
      values.Goal5Date = ''
    }
    if (additionalGoalCount === 1) {
      values.Goal4Text = ''
      values.Goal4Date = ''
      values.Goal5Text = ''
      values.Goal5Date = ''
    }
    if (additionalGoalCount === 0) {
      values.Goal3Text = ''
      values.Goal3Date = ''
      values.Goal4Text = ''
      values.Goal4Date = ''
      values.Goal5Text = ''
      values.Goal5Date = ''
    }
    const postBody = {
      flowName: PLAYBOOK_STEP_MAP.flowName,
      name: PLAYBOOK_STEP_MAP.name,
      publishDate: PLAYBOOK_STEP_MAP.publishDate,
      siteId: siteId,
      structureVersion,
    }
    const updateBody = {
      id: publishFlowId,
      siteId,
      publishDate: PLAYBOOK_STEP_MAP.publishDate,
      items: [],
    }
    const completeOrDraftField = isComplete ? 'completeValue' : 'draftValue'
    let fieldNamesToUpdate = [...requiredFieldNames]

    if (values.LessonCompletionGoalIsDisabled) {
      fieldNamesToUpdate = fieldNamesToUpdate.filter(
        fieldName =>
          fieldName !== 'LessonCompletionGoalValue' &&
          fieldName !== 'LessonCompletionGoalDate' &&
          fieldName !== 'ProgramStartDate' &&
          fieldName !== 'LessonCompletionGoalIsDisabled',
      )
    } else {
      fieldNamesToUpdate = fieldNamesToUpdate.filter(
        fieldName => fieldName !== 'LessonCompletionGoalIsDisabled',
      )
    }

    fieldNamesToUpdate.forEach(fieldName => {
      updateBody.items.push({
        name: fieldName,
        [completeOrDraftField]: values[fieldName],
      })
    })

    optionalFieldNames.forEach(fieldName => {
      if (structureVersion !== 1) {
        updateBody.items.push({
          name: fieldName,
          [completeOrDraftField]: values[fieldName],
        })
      }
    })

    if (publishFlowExists) {
      await updatePublishFlows(publishFlowId, updateBody)
      await fetchPublishFlows(siteId)
    } else {
      const { id } = await postPublishFlows(postBody)
      updateBody.id = id
      await updatePublishFlows(id, updateBody)
      await fetchPublishFlows(siteId)
    }
  }

  const handleChangeGoalCount = (delta, index, setFieldValue, values) => {
    const newGoalCount = additionalGoalCount + delta
    const isDecrement = delta === -1

    if (isDecrement && newGoalCount >= 0) {
      if (index !== undefined && index < additionalGoalCount - 1) {
        for (let i = index; i < additionalGoalCount - 1; i++) {
          const currentGoal = additionalGoalFields[i]
          const nextGoal = additionalGoalFields[i + 1]
          setFieldValue(currentGoal.text, values[nextGoal.text])
          setFieldValue(currentGoal.date, values[nextGoal.date])
        }
        const lastGoal = additionalGoalFields[additionalGoalCount - 1]
        setFieldValue(lastGoal.text, null)
        setFieldValue(lastGoal.date, null)
        setFieldValue(lastGoal.show, false)
      } else {
        const adjustedGoalFields = additionalGoalFields[newGoalCount]
        setFieldValue(adjustedGoalFields.date, null)
        setFieldValue(adjustedGoalFields.text, null)
        setFieldValue(adjustedGoalFields.show, false)
      }
    } else if (newGoalCount - 1 >= 0) {
      setFieldValue(additionalGoalFields[newGoalCount - 1].show, true)
      setFieldValue(additionalGoalFields[newGoalCount - 1].text, null)
      setFieldValue(additionalGoalFields[newGoalCount - 1].date, null)
    }

    setAdditionalGoalCount(newGoalCount)
  }

  const handleNextButtonClick = async (
    shouldSave,
    isComplete,
    setSubmitting,
    setTouched,
    values,
  ) => {
    const isValidSync = validationSchema.isValidSync(values)
    if (shouldSave && !isValidSync) {
      const touchedFields = fieldNames.reduce((acc, name) => {
        acc[name] = true
        return acc
      }, {})
      await setTouched(touchedFields)
      return
    }
    if (shouldSave) {
      setSubmitting(true)
      await handleSave(values, isComplete)
      setSubmitting(false)
    }

    if (areRequiredGoalsComplete) {
      closeModal()
    } else {
      handleStepChange(currentStep + 1)
    }
  }

  return (
    <Formik
      enableReinitialize
      initialTouched={{
        ClassCreationGoalDate: !!preferences?.ClassCreationGoalDate,
        ClassCreationGoalValue: !!preferences?.ClassCreationGoalValue,
        LessonCompletionGoalDate: !!preferences?.LessonCompletionGoalDate,
        LessonCompletionGoalValue: !!preferences?.LessonCompletionGoalValue,
        ProgramStartDate: !!preferences?.ProgramStartDate,
      }}
      initialValues={{
        ...preferences,
        ClassCreationGoalDate: preferences?.ClassCreationGoalDate
          ? new Date(preferences.ClassCreationGoalDate)
          : null,
        LessonCompletionGoalDate: preferences?.LessonCompletionGoalDate
          ? new Date(preferences.LessonCompletionGoalDate)
          : null,
        ProgramStartDate: preferences?.ProgramStartDate
          ? new Date(preferences.ProgramStartDate)
          : null,
        LessonCompletionGoalIsDisabled:
          preferences?.LessonCompletionGoalIsDisabled || false,
        ShowGoal3: preferences?.ShowGoal3 || false,
        ShowGoal4: preferences?.ShowGoal4 || false,
        ShowGoal5: preferences?.ShowGoal5 || false,
        Goal3Date: preferences?.Goal3Date
          ? new Date(preferences.Goal3Date)
          : null,
        Goal4Date: preferences?.Goal4Date
          ? new Date(preferences.Goal4Date)
          : null,
        Goal5Date: preferences?.Goal5Date
          ? new Date(preferences.Goal5Date)
          : null,
      }}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        setSubmitting,
        setTouched,
        isSubmitting,
        isValid,
      }) => (
        <GoalsModalComponent
          additionalGoalCount={additionalGoalCount}
          currentStep={currentStep}
          errors={errors}
          flowLabel={flowLabel}
          goBack={() => goBack(setFieldValue)}
          handleCancel={handleCancel}
          handleCancelButtonClick={async (shouldSave, isComplete) => {
            if (shouldSave) {
              setSubmitting(true)
              await handleSave(values, isComplete)
              setSubmitting(false)
            }
            closeModal()
          }}
          handleChange={(name, value) => {
            if (
              name === classCreationGoalValueName ||
              name === lessonCompletionGoalValueName
            ) {
              value = Math.floor(Number(value))
            }
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleChangeGoalCount={(delta, index) =>
            handleChangeGoalCount(delta, index, setFieldValue, values)
          }
          handleClose={() => {
            clearErrors()
            handleClose()
          }}
          handleNextButtonClick={async (shouldSave, isComplete) =>
            handleNextButtonClick(
              shouldSave,
              isComplete,
              setSubmitting,
              setTouched,
              values,
            )
          }
          handleStepChange={handleStepChange}
          hasApiError={hasApiError}
          hideOutro={hideOutro}
          isComplete={areRequiredGoalsComplete}
          isSubmitting={isSubmitting}
          isValid={isValid}
          modals={modals}
          preferences={preferences}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  )
}

const mapStateToProps = (state, ownProps) => {
  const { error, structureVersion } = state.ssapiManager
  const publishFlows = ssapiSelectors.selectPublishFlowsBySiteId(
    state,
    ownProps.siteId,
  )
  const {
    admin: {
      k8: { playbookData },
    },
  } = state.reportsManager || {}
  const publishFlow =
    publishFlows?.find(
      flow => flow.publishFlowStructureId === structureVersion,
    ) || {}
  const publishFlowExists = Object.keys(publishFlow).length > 0
  const publishFlowId = publishFlow?.id || null
  const areRequiredGoalsCompleteMap = {}
  const preferences = {
    previousYear: {},
  }
  const sitePlaybookData = playbookData?.find(
    data => data.site_id === ownProps.siteId,
  )
  preferences.ClassesCreated = sitePlaybookData?.classes_created
  const lessonCompletionPercentage =
    sitePlaybookData?.schoolwide_lesson_completion_percentage === 0
      ? 0
      : sitePlaybookData?.schoolwide_lesson_completion_percentage * 100
  preferences.LessonCompletionPercentage =
    sitePlaybookData?.lessons_marked_done === 0 ? 0 : lessonCompletionPercentage
  requiredFieldNames.forEach(fieldName => {
    const preference = publishFlow?.publishFlowItems?.find(
      item => item.name === fieldName,
    )
    preferences[fieldName] =
      preference?.completeValue || preference?.draftValue || null
    areRequiredGoalsCompleteMap[fieldName] = preference?.completeValue || false
  })
  optionalFieldNames.forEach(fieldName => {
    const preference = publishFlow?.publishFlowItems?.find(
      item => item.name === fieldName,
    )
    if (fieldName === schoolwideGoalIsDisabledName) {
      if (
        preference?.completeValue === 'true' ||
        preference?.draftValue === 'true'
      ) {
        preferences[fieldName] = true
      } else if (
        preference?.completeValue === 'false' ||
        preference?.draftValue === 'false'
      ) {
        preferences[fieldName] = false
      } else {
        preferences[fieldName] = null
      }
    } else {
      preferences[fieldName] =
        preference?.completeValue || preference?.draftValue || null
    }
  })
  const previousYearPublishFlow =
    structureVersion > 1
      ? publishFlows?.find(
          flow => flow.publishFlowStructureId === structureVersion - 1,
        )
      : publishFlows?.find(flow => flow.publishFlowStructureId === 1)
  previousYearFieldNames.forEach(fieldName => {
    const preference = previousYearPublishFlow?.publishFlowItems?.find(
      item => item.name === fieldName,
    )
    preferences.previousYear[fieldName] = preference?.completeValue || null
  })
  const currentYear = publishFlow?.publishedDate
    ? Number(publishFlow?.publishedDate.slice(0, 4))
    : new Date().getFullYear().toString()
  if (currentYear !== null) {
    const previousYear = currentYear - 1
    const shortCurrentYear = String(currentYear).slice(-2)
    const previousYearText = `${previousYear}-${shortCurrentYear}`

    preferences.previousYear = {
      ...preferences.previousYear,
      previousYearText,
    }
  }
  const areRequiredGoalsComplete = Object.values(
    areRequiredGoalsCompleteMap,
  ).every(value => !!value)

  return {
    areRequiredGoalsComplete,
    hasApiError: !!error,
    preferences,
    publishFlowExists,
    publishFlowId,
    structureVersion,
  }
}

const mapDispatchToProps = {
  fetchPublishFlows: ssapiOperations.fetchPublishFlows,
  postPublishFlows: ssapiOperations.postPublishFlows,
  updatePublishFlows: ssapiOperations.updatePublishFlows,
  clearErrors: ssapiOperations.clearErrors,
}

GoalsModalContainer.propTypes = {
  areRequiredGoalsComplete: PropTypes.bool,
  clearErrors: PropTypes.func,
  currentStep: PropTypes.number,
  fetchPublishFlows: PropTypes.func,
  flowLabel: PropTypes.string,
  handleCancel: PropTypes.func,
  handleClose: PropTypes.func,
  hasApiError: PropTypes.bool,
  hideOutro: PropTypes.bool,
  modals: PropTypes.array,
  postPublishFlows: PropTypes.func,
  preferences: PropTypes.object,
  publishFlowExists: PropTypes.bool,
  publishFlowId: PropTypes.number,
  siteId: PropTypes.number,
  structureVersion: PropTypes.number,
  updatePublishFlows: PropTypes.func,
}

const mapper = entry => {
  const { flowLabel, modals } = entry || {}
  return {
    flowLabel,
    modals,
  }
}

const options = {
  entryId: '5hOmSQFwOUHZlaHbC6Uia2',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    GoalsModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
